import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import usePageTitle from '../hooks/usePageTitle';
import loginErrorMessage from '../auth/loginErrorMessage';
import routeForUser, { LOGIN_ROUTE } from '../auth/routeForUser';

function LoginPage() {
  usePageTitle('Log In');

  const navigate = useNavigate();
  const { login, isAuthenticated, user, loading: authLoading } = useApp();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated. The rule itself lives in
  // routeForUser, which the home page applies too.
  useEffect(() => {
    if (authLoading) {
      return;
    }
    const destination = routeForUser({ isAuthenticated, user });
    if (destination !== LOGIN_ROUTE) {
      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);
    
    try {
      await login(email, password);
      // Navigation will be handled by the useEffect above
    } catch (err) {
      setError(loginErrorMessage(err?.code));
      setLoading(false);
    }
  };

  const inputClasses = (hasError = false) => `
    w-full px-4 py-3 border rounded-lg transition-all duration-200
    ${hasError 
      ? 'border-red-400 focus:ring-2 focus:ring-red-200' 
      : 'border-gray-300 focus:ring-2 focus:ring-red-100 focus:border-red-400'}
  `;

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F8F9FB' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#a60533' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F8F9FB' }}>
      {/* Header */}
      <nav className="bg-white shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center p-4">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="One Stop Greek Logo" 
                className="h-10 w-10"
              />
              <span className="text-2xl font-bold" style={{ color: '#a60533' }}>
                One Stop Greek
              </span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Logo and Title */}
            <div className="text-center mb-8">
              <img 
                src="/logo.png" 
                alt="One Stop Greek" 
                className="h-20 w-20 mx-auto mb-4"
              />
              <h1 className="text-3xl font-bold text-gray-900">Welcome Back</h1>
              <p className="text-gray-600 mt-2">Sign in to your account</p>
            </div>

            {error && (
              <div role="alert" className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {notice && (
              <div role="status" className="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-blue-800 text-sm">
                  {notice}{' '}
                  <a
                    href="mailto:support@onestopgreek.com"
                    className="underline font-medium"
                  >
                    support@onestopgreek.com
                  </a>
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={inputClasses()}
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={inputClasses()}
                  required
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                {/* Password reset by email is not built yet. The notice says
                    so in the page; a browser alert blocks the page and looks
                    nothing like the site. */}
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setNotice(
                      'Password reset from the website is not available yet. ' +
                      'For a reset, write to'
                    );
                  }}
                  className="font-medium hover:underline"
                  style={{ color: '#a60533' }}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 text-white rounded-lg font-semibold transition-all duration-200 flex items-center justify-center"
                style={{ 
                  backgroundColor: loading ? '#ccc' : '#a60533',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#8a0429')}
                onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#a60533')}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 border-t border-gray-300"></div>
              <span className="px-4 text-sm text-gray-500">OR</span>
              <div className="flex-1 border-t border-gray-300"></div>
            </div>

            {/* Create Account Link */}
            <Link
              to="/create-account"
              className="block w-full py-3 px-4 text-center rounded-lg font-semibold border-2 transition-all duration-200"
              style={{ color: '#a60533', borderColor: '#a60533' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#fce7ed'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Create an Account
            </Link>

            <p className="text-center text-sm text-gray-500 mt-6">
              By signing in, you agree to our{' '}
              <Link to="/privacy-policy" className="underline" style={{ color: '#a60533' }}>
                Privacy Policy
              </Link>
            </p>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-6">
            <Link 
              to="/" 
              className="text-sm text-gray-600 hover:underline"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
